import { loadAndMountMfe, unmountMfe } from './mfe-loader';
import { showMfeLoader, hideMfeLoader, setLoaderGate } from './mfe-loading-indicator';
import {
  type HrmConfig,
  type HrmMfe,
  getHrmConfig,
  type PushStateCallback,
  // userAuthenticated,
  userAuthenticatedEventName,
  hrmI18n,
  pendoService,
  datadogRumService,
  hrmUser,
  consoleFlags,
  setActiveNavContext,
} from '@platform-services/hrm-mfe-global';
import { NoPathRouteStatus, resolveDefaultRoute, contextForPath } from '@platform-services/hrm-nav';

window.globalThis.hrmPushState = new Map();

// configure() is side-effect-free; init() runs from HrmUser#initializeDatadog
// after consent (org.allowAnalytics from /whoami) is known. Mirrors the Pendo
// gate so we never write the _dd_s tracking cookie pre-consent.
const parseRate = (v: string | undefined, fallback: number): number => {
  if (v === undefined || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
datadogRumService.configure({
  // VITE_DD_* are `string | undefined`; the required fields fall back to '' so
  // the config is well-typed. configure() only stores it, and start()'s init
  // guard treats '' and undefined identically (skips RUM init), so an empty
  // fallback is behaviourally equivalent to a missing var.
  applicationId: import.meta.env.VITE_DD_APPLICATION_ID ?? '',
  clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN ?? '',
  site: import.meta.env.VITE_DD_SITE,
  env: import.meta.env.VITE_ENV ?? '',
  version: import.meta.env.VITE_VERSION,
  sessionSampleRate: parseRate(import.meta.env.VITE_DD_SAMPLE_RATE, 100),
  sessionReplaySampleRate: parseRate(import.meta.env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE, 0),
});

pendoService.init(import.meta.env.VITE_PENDO_API_KEY, import.meta.env.VITE_PENDO_APP_ENV);

window.history.pushState = new Proxy(window.history.pushState, {
  apply: async (
    target,
    thisArg,
    argArray: [data: never, unused: string, url: string | URL | null | undefined],
  ) => {
    window.globalThis.hrmPushState.forEach(fn => {
      fn(argArray); // call all registered pushStateFunc
    });
    return target.apply(thisArg, argArray); // call original pushState method
  },
});

const mfeConfig: HrmConfig = getHrmConfig();

const sidebarMfeKey = '@knowbe4hrm/sidebar-mfe';
const topbarMfeKey = '@knowbe4hrm/topbar-mfe';
const utilitiesMfeKey = '@knowbe4hrm/utilities-mfe';
const defaultMfeKey = '@knowbe4hrm/default-mfe';

const defaultMfe = mfeConfig.mfeServices.find(service => service.importKey === defaultMfeKey)!;
const bodyMfes = mfeConfig.mfeServices.filter(x => x.urlPathPrefix);

let mountedContentMfe: HrmMfe | undefined = undefined;

consoleFlags.bind(import.meta.env.VITE_APP_LAUNCHDARKLY_CLIENT_SIDE_ID);

// Initialize i18n early so translations are available for any errors during startup
await hrmI18n.init();

// Body-content KbLoader waits for the sidebar's first paint before appearing,
// so users see the chrome (sidebar + topbar) settle before a spinner shows up
// inside it. Resolved immediately for routes that never mount a sidebar.
let resolveSidebarReady!: () => void;
const sidebarReadyPromise = new Promise<void>(r => (resolveSidebarReady = r));
setLoaderGate(sidebarReadyPromise);

// 1. Check if the user is trying to get to the default mfe (error, 404, etc)
const pathSections = window.location.pathname.toLowerCase().split('/').filter(Boolean);
const isCorePath =
  pathSections[0] === 'core' || pathSections[0] === 'not-found' || pathSections[0] === 'error';
const isOnboardingPath = pathSections[0] === 'onboarding';
if (isCorePath) {
  resolveSidebarReady();
  await loadMfe(defaultMfe); // This will mount core MFE pages without any other MFEs
} else if (isOnboardingPath) {
  resolveSidebarReady();
  const settingsMfe = bodyMfes.find(x => x.importKey === '@knowbe4hrm/settings-mfe');
  await loadMfe(settingsMfe ?? defaultMfe);
} else {
  // 1. Check if the user is logged in
  window.addEventListener(userAuthenticatedEventName, async () => {
    await onUserAuthenticated();
  });

  // TODO these are removed for testing
  const authMfeKey = '@knowbe4hrm/auth-mfe';
  await loadAndMountMfe(authMfeKey, 'mfe:@knowbe4hrm/auth-mfe');
  // TODO this is just here for testing
  // userAuthenticated();

  async function onUserAuthenticated() {
    const mfePath = new URL(window.location.toString()).pathname
      .toLowerCase()
      .split('/')
      .filter(x => x !== '')[0];

    if (!mfePath) {
      // Single source of truth for the pathless-root landing route (hrm-nav): resolves the LX nav
      // flag and the destination table together, so the redirect here and the sidebar's nav-item
      // highlight can't disagree. Falls back to the legacy LX dashboard if LD is unreachable.
      const destination = await resolveDefaultRoute(hrmUser);

      if (destination.status === NoPathRouteStatus.OK) {
        window.history.replaceState({}, '', destination.path);
      } else {
        window.location.replace('/error');
      }
    }

    // Load HRM MFEs
    await initHrm();
  }
}

async function initHrm() {
  addMfeNavigationEventHandlers();

  // this is temporary as utilities-mfe mounting responsibility is handed back to mfe
  // only keep this logic here because the mfe is not deployed to prod yet so no mounting at all if ff is off
  await consoleFlags.ensureReady();
  const mountUtilities = consoleFlags.getBool('hrm-console-utility-bar', false);

  const sidebarLoad = loadAndMountMfe(sidebarMfeKey, 'mfe:@knowbe4hrm/sidebar-mfe');
  // Open the loader gate one paint frame after sidebar mount so the sidebar's
  // first render lands before the body KbLoader appears inside it.
  sidebarLoad.then(() => requestAnimationFrame(() => resolveSidebarReady()));
  await Promise.all([
    sidebarLoad,
    loadAndMountMfe(topbarMfeKey, 'mfe:@knowbe4hrm/topbar-mfe'),
    ...(mountUtilities ? [loadAndMountMfe(utilitiesMfeKey, 'mfe:@knowbe4hrm/utilities-mfe')] : []),
  ]);
  switchMfe(new URL(window.location.toString()).pathname);
}

// Hide the currently-mounted body MFE synchronously when an incoming
// navigation targets a DIFFERENT MFE. Our popstate/pushState handler runs
// before the outgoing MFE's own vue-router popstate listener (we register
// first, during initHrm, before any body MFE mounts). Without this, the
// outgoing MFE's vue-router matches the new URL, fails, and paints its own
// 404 in the window between popstate and the async unmount + swap completing.
// Same-MFE internal nav must NOT hide, or every in-app route change would
// flash blank.
function hideOutgoingMfeIfSwapping(path: string): void {
  if (!mountedContentMfe) return;
  const mfePath =
    path
      .toLowerCase()
      .split('/')
      .filter(x => x !== '')[0] ?? '';
  const foundMfe = mfePath ? bodyMfes.find(x => x.urlPathPrefix === mfePath) : undefined;
  const resolved = foundMfe ?? defaultMfe;
  if (resolved.importKey === mountedContentMfe.importKey) return;
  const currentMfeDiv = document.getElementById(`mfe:${mountedContentMfe.importKey}`);
  if (currentMfeDiv) currentMfeDiv.style.visibility = 'hidden';
}

function addMfeNavigationEventHandlers() {
  // Read window.location.pathname rather than e.state.current: state.current is
  // written by each MFE's internal vue-router relative to ITS OWN base, so on
  // native back/forward it can be a bare sub-path (e.g. `/browse` inside
  // /sat-modstore/) that switchMfe can't resolve to the owning MFE. See
  // ESAT-71350 regression test in src/tests/unit/main.test.ts.
  window.addEventListener('popstate', async () => {
    const path = window.location.pathname;
    hideOutgoingMfeIfSwapping(path);
    switchMfe(path);
  });

  const pushState: PushStateCallback = async argArray => {
    let path: string | undefined;
    if (argArray[2] instanceof URL) {
      path = argArray[2].pathname;
    } else if (argArray[2]) {
      const parsedUrl = URL.parse(argArray[2] as string);
      path = parsedUrl?.pathname ?? (argArray[2] as string);
    }
    if (path === undefined) return;
    hideOutgoingMfeIfSwapping(path);
    switchMfe(path);
  };

  window.globalThis.hrmPushState.set('mfe-navigation', pushState);

  // Only scroll on real page changes — not when an MFE pushes new query
  // params (filters, modals, pagination, tabs) onto the same pathname.
  let lastScrolledPath = window.location.pathname;
  window.globalThis.hrmPushState.set('scroll-to-top', argArray => {
    const url = argArray[2];
    const newPath =
      url instanceof URL
        ? url.pathname
        : url
          ? (URL.parse(url as string)?.pathname ?? lastScrolledPath)
          : lastScrolledPath;
    if (newPath !== lastScrolledPath) {
      lastScrolledPath = newPath;
      smoothScrollToTop(700);
    }
  });
}
// Glides the page back to the top after a route change, so the user doesn't
// land halfway down a new page. We roll our own instead of using the native
// `behavior: 'smooth'` because that one's too fast and we can't tune it.
function smoothScrollToTop(durationMs: number) {
  // Honour the OS "reduce motion" setting — instant jump, no animation.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo(0, 0);
    return;
  }

  const startY = window.scrollY;
  if (startY === 0) return; // already at the top

  const startTime = performance.now();

  // easeInOutCubic — slow at the ends, quick in the middle. Feels natural.
  const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  // Each frame, work out how far along we are and scroll the viewport that
  // much closer to 0. Stop once we're there.
  const animateFrame = (now: number) => {
    const progress = Math.min(1, (now - startTime) / durationMs);
    window.scrollTo(0, startY * (1 - ease(progress)));
    if (progress < 1) requestAnimationFrame(animateFrame);
  };

  requestAnimationFrame(animateFrame);
}

async function switchMfe(path: string) {
  if (!path) return;

  const mfePath =
    path
      .toLowerCase()
      .split('/')
      .filter(x => x !== '')[0] ?? '';

  const foundMfe = mfePath ? bodyMfes.find(x => x.urlPathPrefix === mfePath) : undefined;
  const resolved = foundMfe ?? defaultMfe;

  // Broadcast the coarse (shell-tier) active context. A fall back to default-mfe (unmatched segment,
  // /not-found, system routes) is neutral; otherwise contextForPath's exclusion rule applies
  // (LX prefixes → training, else admin). The active MFE may refine this via publishMfeContext.
  setActiveNavContext({
    coarse: resolved === defaultMfe ? 'neutral' : contextForPath(path),
    activeMfe: resolved.importKey,
    basePath: mfePath,
  });

  await loadMfe(resolved);
}

async function loadMfe(mfe: HrmMfe) {
  try {
    if (mountedContentMfe?.importKey !== mfe.importKey) {
      if (mountedContentMfe) {
        await unmountMfe(mountedContentMfe.importKey);
      }
      const mfeContainerDiv = document.getElementById('mfe-content')!;
      while (mfeContainerDiv.firstChild) {
        mfeContainerDiv.removeChild(mfeContainerDiv.firstChild);
      }
      const mfeDivKey = `mfe:${mfe.importKey}`;

      const mfeDiv = document.createElement('div');
      mfeDiv.setAttribute('id', mfeDivKey);
      // Keep the incoming MFE hidden while it mounts, then give it one paint
      // frame to finish reconciling before revealing. Warm-mounted MFEs (e.g.
      // modstore3) re-attach a cached Vue DOM subtree whose vue-router may have
      // heard popstate for a foreign URL while unmounted and transitioned Nuxt
      // into its 404 error state; without this delay, that stale error page
      // paints for a frame before Vue re-renders the correct route.
      mfeDiv.style.visibility = 'hidden';
      mfeContainerDiv.appendChild(mfeDiv);
      showMfeLoader(mfeContainerDiv);
      loadAndMountMfe(mfe.importKey, mfeDivKey).finally(() => {
        requestAnimationFrame(() => {
          mfeDiv.style.visibility = '';
        });
        hideMfeLoader(mfeContainerDiv);
      });
      mountedContentMfe = mfe;
    }
  } catch (error) {
    console.error(`Failed to load MFE: ${mfe?.importKey}`, error);
  }
}
